import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css'; 
import { db, auth } from "./firebase"; 
import { ref, set, onValue, update, remove, get } from "firebase/database";
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut } from "firebase/auth";

// ==========================================
// SECTION 1: GLOBAL CONSTANTS & SETTINGS
// ==========================================
const MAX_CAPS = { "Cage": 2, "Small Run": 8, "Large Run": 4, "Cat Kennel": 6 };

const cleanDigits = (val) => (val ? val.toString().replace(/[^\d]/g, '') : '');
const cleanKey = (val) => (val ? val.toString().toLowerCase().replace(/[\.\#\$\/\[\]]/g, '').trim() : '');

const formatPhone = (val) => {
  if (!val) return val;
  const num = cleanDigits(val);
  if (num.length < 4) return num;
  if (num.length < 7) return `${num.slice(0, 3)}-${num.slice(3)}`;
  return `${num.slice(0, 3)}-${num.slice(3, 6)}-${num.slice(6, 10)}`;
};

function App() {
  // ==========================================
  // SECTION 2: MAIN APP STATES
  // ==========================================
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [allBookings, setAllBookings] = useState([]);
  const [profile, setProfile] = useState(null);
  const [allProfiles, setAllProfiles] = useState([]);
  const [bannedList, setBannedList] = useState([]);
  const [view, setView] = useState("login");

  // ==========================================
  // SECTION 3: FIREBASE CONNECTIVITY
  // ==========================================
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setIsAdmin(currentUser?.email === "admin@clinic.com");
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    onValue(ref(db, 'bookings/'), (snap) => setAllBookings(snap.val() ? Object.keys(snap.val()).map(k => ({ id: k, ...snap.val()[k] })) : []));
    onValue(ref(db, 'banned/'), (snap) => setBannedList(snap.val() ? Object.keys(snap.val()).map(k => ({ id: k, ...snap.val()[k] })) : []));
    onValue(ref(db, 'users/'), (snap) => {
      if (snap.val()) {
        const raw = snap.val();
        const profiles = Object.keys(raw).map(u => ({ uid: u, ...raw[u].profile }));
        setAllProfiles(profiles);
      } else {
        setAllProfiles([]);
      }
    });
    if (user) onValue(ref(db, `users/${user.uid}/profile`), (snap) => setProfile(snap.val()));
  }, [user]);

  if (isAdmin) return <StaffDashboard logout={() => signOut(auth)} allBookings={allBookings} allProfiles={allProfiles} bannedList={bannedList} />;

  return (
    <div style={pageLayout}>
      <header style={headerStyle}>
        <div style={{display:'flex', flexDirection:'column'}}>
           <h1 style={{margin:0, fontSize:'1.5rem'}}>ANIMAL CLINIC OF LAWRENCEVILLE</h1>
           <span style={{fontSize:'0.9rem', color:'#f1c40f', fontWeight:'bold', letterSpacing:'1px'}}>🐾 BOARDING PORTAL</span>
        </div>
        {user && <button onClick={() => signOut(auth)} style={logoutSmall}>Logout</button>}
      </header>
      {!user ? (
        <section style={containerStyle}>
          <div style={cardStyle}>
            <div style={{textAlign:'center', marginBottom:'15px'}}>
               <h3 style={{margin:0, color:'#1a5276'}}>Welcome!</h3>
               <p style={{fontSize:'12px', color:'#777'}}>Please login or sign up to manage your pet's boarding stay.</p>
            </div>
            {view === "login" ? <Login setView={setView} /> : <Signup setView={setView} allProfiles={allProfiles}/>}
          </div>
        </section>
      ) : (
        <OwnerDashboard user={user} allBookings={allBookings} profile={profile} bannedList={bannedList} allProfiles={allProfiles} />
      )}
    </div>
  );
}

// ==========================================
// SECTION 5: STAFF DASHBOARD
// ==========================================
function StaffDashboard({ logout, allBookings, allProfiles, bannedList }) {
  const [filterDate, setFilterDate] = useState(new Date());
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBanned, setShowBanned] = useState(false);
  const [scheduleSearch, setScheduleSearch] = useState(""); 
  const [banName, setBanName] = useState("");
  const [banOwnerFirst, setBanOwnerFirst] = useState("");
  const [banOwnerLast, setBanOwnerLast] = useState("");
  const [familySync, setFamilySync] = useState(null);

  const daily = allBookings.filter(b => b.status !== "Pending" && b.status !== "Declined" && filterDate.getTime() >= b.startVal && filterDate.getTime() <= b.endVal);
  const pending = allBookings.filter(b => b.status === "Pending");
  const declined = allBookings.filter(b => b.status === "Declined");

  const filteredDaily = daily.filter(b => {
    const s = scheduleSearch.toLowerCase();
    return (b.petName?.toLowerCase().includes(s) || b.ownerLastName?.toLowerCase().includes(s) || b.clientNumber?.toString().includes(s));
  });

  const groupedDaily = filteredDaily.reduce((groups, booking) => {
    const owner = booking.ownerLastName?.toUpperCase() || "UNKNOWN";
    if (!groups[owner]) groups[owner] = [];
    groups[owner].push(booking);
    return groups;
  }, {});

  const handleFamilySync = (newDates) => {
    const startVal = newDates[0].getTime();
    const endVal = newDates[1].getTime();
    const startDate = newDates[0].toDateString();
    const endDate = newDates[1].toDateString();
    groupedDaily[familySync].forEach(pet => {
      update(ref(db, `bookings/${pet.id}`), { startVal, endVal, startDate, endDate });
    });
    setFamilySync(null);
  };

  const handleDeny = (id) => {
    const reason = window.prompt("Reason for declining this booking?");
    if (reason !== null) update(ref(db, `bookings/${id}`), { status: 'Declined', declineReason: reason });
  };

  const handleBan = () => {
    if (!banName || !banOwnerLast) return alert("Enter Pet Name and Owner Last Name.");
    const id = Date.now();
    set(ref(db, `banned/${id}`), { petName: banName.toLowerCase().trim(), ownerFirstName: banOwnerFirst.toLowerCase().trim(), ownerLastName: banOwnerLast.toLowerCase().trim(), id });
    setBanName(""); setBanOwnerFirst(""); setBanOwnerLast("");
  };

  const consolidateMasterRoster = async () => {
    if (!window.confirm("This will organize all clients into a single clean roster, link all pets to their correct owner, and remove ghost duplicates. Ready to proceed?")) return;

    try {
      const uSnap = await get(ref(db, 'users/'));
      const rawUsers = uSnap.val() || {};
      const bSnap = await get(ref(db, 'bookings/'));
      const rawBookings = bSnap.val() || {};

      const masterMap = {}; 
      const keyMapping = {}; 

      // 1. Ingest existing user accounts
      Object.keys(rawUsers).forEach(k => {
        const prof = rawUsers[k]?.profile || {};
        const cNum = cleanKey(prof.clientNumber);
        const ph = cleanDigits(prof.phone);
        const lName = cleanKey(prof.lastName);
        const fName = cleanKey(prof.firstName);

        let masterKey = null;
        if (cNum) masterKey = `CLIENT-${cNum}`;
        else if (ph) masterKey = `CLIENT-${ph}`;
        else if (lName && fName) masterKey = `CLIENT-${lName}-${fName}`;
        else masterKey = cleanKey(k);

        keyMapping[k] = masterKey;

        if (!masterMap[masterKey]) {
          masterMap[masterKey] = {
            profile: {
              firstName: prof.firstName || '',
              lastName: prof.lastName || '',
              phone: formatPhone(prof.phone) || '',
              emergencyContact: prof.emergencyContact || '',
              clientNumber: prof.clientNumber?.toString().trim() || '',
              uid: masterKey,
              pets: {}
            }
          };
        }

        if (prof.pets) {
          Object.keys(prof.pets).forEach(pKey => {
            const pet = prof.pets[pKey];
            if (pet && pet.name) {
              const petUnique = cleanKey(pet.name) || 'pet';
              masterMap[masterKey].profile.pets[petUnique] = {
                name: pet.name.trim(),
                run: pet.run || 'Small Run',
                temperament: pet.temperament || '',
                meds: pet.meds || ''
              };
            }
          });
        }
      });

      // 2. Scan all bookings to merge unlisted pets and correct IDs
      const bookingUpdates = {};
      Object.keys(rawBookings).forEach(bId => {
        const b = rawBookings[bId];
        const cNum = cleanKey(b.clientNumber);
        const ph = cleanDigits(b.ownerPhone);
        const lName = cleanKey(b.ownerLastName);
        const fName = cleanKey(b.ownerFirstName);

        let targetKey = null;
        if (cNum) targetKey = `CLIENT-${cNum}`;
        else if (ph) targetKey = `CLIENT-${ph}`;
        else if (keyMapping[b.userId]) targetKey = keyMapping[b.userId];
        else if (lName && fName) targetKey = `CLIENT-${lName}-${fName}`;
        else targetKey = cleanKey(b.userId) || 'CLIENT-UNKNOWN';

        bookingUpdates[`bookings/${bId}/userId`] = targetKey;

        if (!masterMap[targetKey]) {
          masterMap[targetKey] = {
            profile: {
              firstName: b.ownerFirstName || '',
              lastName: b.ownerLastName || '',
              phone: formatPhone(b.ownerPhone) || '',
              emergencyContact: b.emergencyContact || '',
              clientNumber: b.clientNumber?.toString().trim() || '',
              uid: targetKey,
              pets: {}
            }
          };
        } else {
          if (!masterMap[targetKey].profile.clientNumber && b.clientNumber) masterMap[targetKey].profile.clientNumber = b.clientNumber.toString().trim();
          if (!masterMap[targetKey].profile.emergencyContact && b.emergencyContact) masterMap[targetKey].profile.emergencyContact = b.emergencyContact;
        }

        if (b.petName) {
          const petUnique = cleanKey(b.petName) || 'pet';
          masterMap[targetKey].profile.pets[petUnique] = {
            name: b.petName.trim(),
            run: b.runType || 'Small Run',
            temperament: b.temperament || '',
            meds: b.meds || ''
          };
        }
      });

      // 3. Write clean roster back to Firebase
      await set(ref(db, 'users'), masterMap);
      if (Object.keys(bookingUpdates).length > 0) {
        await update(ref(db), bookingUpdates);
      }

      alert("Roster successfully consolidated! All illegal characters cleaned and ghost profiles replaced by unified master client files.");
    } catch (err) {
      alert("Consolidation error: " + err.message);
    }
  };

  return (
    <div style={{maxWidth:'1200px', margin:'0 auto', padding:'20px', fontFamily:'sans-serif'}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}><h2>Staff Portal: Lawrenceville Boarding</h2><button onClick={logout} style={logoutSmall}>Logout</button></div>
      <div style={{display:'flex', gap:'20px', marginTop:'20px'}}>
        <div style={{flex: 1.2}}>
          <div style={cardStyle}>
            <h4 style={{marginTop:0}}>📊 Run Availability</h4>
            {Object.keys(MAX_CAPS).map(run => {
              const activeStays = daily.filter(b => b.runType === run && b.status !== "Checked-Out");
              let groupKeys = new Set();
              let singleRunsCount = 0;

              activeStays.forEach(b => {
                if (b.sharedRunGroup && b.sharedRunGroup !== "none") {
                  groupKeys.add(`${b.ownerLastName}-${b.sharedRunGroup}`);
                } else {
                  singleRunsCount++;
                }
              });

              const totalCapacityUsed = singleRunsCount + groupKeys.size;
              
              return (
                <div key={run} style={{fontSize:'13px', marginBottom:'5px'}}>
                  {run}: <strong>{MAX_CAPS[run] - totalCapacityUsed}</strong> left
                </div>
              );
            })}
          </div>
          <button onClick={()=>setShowAddForm(!showAddForm)} style={{...primaryBtn, backgroundColor:'#1a5276', marginTop:'15px'}}>{showAddForm ? "Close" : "+ Manual Boarding Entry"}</button>
          {showAddForm && <BookingForm isStaff={true} allProfiles={allProfiles} allBookings={allBookings} bannedList={bannedList} close={()=>setShowAddForm(false)} />}
          
          <button onClick={consolidateMasterRoster} style={{...primaryBtn, backgroundColor:'#27ae60', marginTop:'15px'}}>🪄 Convert & Consolidate Master Roster</button>

          <div style={{...cardStyle, marginTop:'15px'}}>
            <h4 style={{color:'#3498db', marginTop:0}}>📩 Pending ({pending.length})</h4>
            {pending.map(b => (
              <div key={b.id} style={miniActionCard}>
                <strong>{b.ownerLastName?.toUpperCase()}, {b.petName}</strong>
                <div style={{display:'flex', gap:'5px', marginTop:'5px'}}>
                  <button onClick={()=>update(ref(db, 'bookings/'+b.id), {status:'Approved'})} style={{...statusBtn, flex:1, backgroundColor: '#3498db'}}>Approve</button>
                  <button onClick={()=>handleDeny(b.id)} style={{...statusBtn, flex:1, backgroundColor: '#e74c3c'}}>Deny</button>
                </div>
              </div>
            ))}
          </div>

          {declined.length > 0 && (
            <div style={{...cardStyle, marginTop:'15px', borderLeft:'4px solid #777'}}>
              <h4 style={{color:'#777', marginTop:0}}>🚫 Declined Requests</h4>
              {declined.map(b => (
                <div key={b.id} style={{fontSize:'11px', borderBottom:'1px solid #eee', paddingBottom:'5px', marginBottom:'5px', position:'relative'}}>
                  <span onClick={()=>remove(ref(db, `bookings/${b.id}`))} style={{position:'absolute', right:0, color:'red', cursor:'pointer', fontWeight:'bold'}}>X</span>
                  <strong>{b.petName} ({b.ownerLastName})</strong>
                  <div style={{color:'#e74c3c'}}>Reason: {b.declineReason || "No reason given."}</div>
                </div>
              ))}
            </div>
          )}

          <button onClick={()=>setShowBanned(!showBanned)} style={{...primaryBtn, backgroundColor:'#e74c3c', marginTop:'15px'}}>🚫 Banned List</button>
          {showBanned && (
             <div style={{...cardStyle, marginTop:'10px', border:'2px solid #e74c3c'}}>
                <input placeholder="Pet Name" value={banName} onChange={e=>setBanName(e.target.value)} style={inputStyle} />
                <input placeholder="Owner First" value={banOwnerFirst} onChange={e=>setBanOwnerFirst(e.target.value)} style={inputStyle} />
                <input placeholder="Owner Last" value={banOwnerLast} onChange={e=>setBanOwnerLast(e.target.value)} style={inputStyle} />
                <button onClick={handleBan} style={{...primaryBtn, backgroundColor:'#e74c3c', marginBottom:'10px'}}>Ban Pet</button>
                {bannedList.map(i => (
                    <div key={i.id} style={{fontSize:'12px', display:'flex', justifyContent:'space-between', marginBottom:'4px'}}>
                        <span>{i.petName.toUpperCase()} ({i.ownerFirstName} {i.ownerLastName})</span>
                        <span onClick={()=>remove(ref(db, `banned/${i.id}`))} style={{color:'red', cursor:'pointer', fontWeight:'bold'}}>X</span>
                    </div>
                ))}
             </div>
          )}
        </div>
        <div style={{flex: 2}}>
          <div style={cardStyle}>
            <Calendar onChange={setFilterDate} value={filterDate} />
            <div style={{marginTop:'15px', position:'relative'}}>
               <input style={{...inputStyle, border:'2px solid #1a5276', paddingLeft:'30px'}} placeholder="🔍 Search current schedule..." value={scheduleSearch} onChange={(e) => setScheduleSearch(e.target.value)} />
               <span style={{position:'absolute', left:'10px', top:'8px'}}>🔎</span>
            </div>
            <h4 style={{borderBottom:'2px solid #eee', paddingBottom:'10px'}}>Schedule: {filterDate.toDateString()}</h4>
            {Object.keys(groupedDaily).map(owner => {
              const firstPet = groupedDaily[owner][0];
              const runGroups = groupedDaily[owner].reduce((acc, pet) => {
                const g = pet.sharedRunGroup || "none";
                if (!acc[g]) acc[g] = [];
                acc[g].push(pet);
                return acc;
              }, {});

              return (
                <div key={owner} style={groupContainer}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px', borderBottom:'1px solid #ddd'}}>
                    <div style={groupHeader}>{owner} FAMILY <span style={{fontSize:'11px', fontWeight:'normal', marginLeft:'10px'}}>📞 {firstPet.ownerPhone}</span></div>
                    <button onClick={() => setFamilySync(familySync === owner ? null : owner)} style={{...statusBtn, backgroundColor:'#f39c12', marginBottom:'5px'}}>📅 Sync Stay</button>
                  </div>
                  {familySync === owner && <div style={{marginBottom:'15px'}}><Calendar selectRange={true} onChange={handleFamilySync} /></div>}
                  
                  {Object.keys(runGroups).map(groupName => {
                    if (groupName !== "none") {
                      return (
                        <div key={groupName} style={{ border: '2px dashed #f39c12', padding: '10px', borderRadius: '8px', marginBottom: '10px', backgroundColor: '#fffdf4' }}>
                          <div style={{ fontSize: '11px', color: '#e67e22', fontWeight: 'bold', marginBottom: '8px' }}>🏡 Occupying Same Run ({runGroups[groupName][0].runType} — {groupName.toUpperCase()})</div>
                          {runGroups[groupName].map(b => <StaffPetCard key={b.id} b={b} allProfiles={allProfiles} filterDate={filterDate} />)}
                        </div>
                      );
                    } else {
                      return runGroups[groupName].map(b => <StaffPetCard key={b.id} b={b} allProfiles={allProfiles} filterDate={filterDate} />);
                    }
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// SECTION 6: STAFF PET CARD
// ==========================================
function StaffPetCard({ b, allProfiles, filterDate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [showNotePopup, setShowNotePopup] = useState(false);
  const [editData, setEditData] = useState({ ...b });
  const [tempNote, setTempNote] = useState(b.stayNotes || b.doctorNote || "");

  const handleStatusChange = () => {
    let nextStatus = b.status === 'Approved' ? 'Checked-In' : b.status === 'Checked-In' ? 'Checked-Out' : 'Approved';
    let timeKey = nextStatus === 'Checked-In' ? 'checkInTime' : 'checkOutTime';
    update(ref(db, 'bookings/'+b.id), { status: nextStatus, [timeKey]: new Date().toLocaleString() });
  };

  const saveEdit = () => {
    update(ref(db, 'bookings/' + b.id), editData);
    if (editData.userId) {
        update(ref(db, `users/${editData.userId}/profile`), { 
          phone: formatPhone(editData.ownerPhone), 
          firstName: editData.ownerFirstName, 
          lastName: editData.ownerLastName, 
          emergencyContact: editData.emergencyContact || "" 
        });
    }
    setIsEditing(false);
  };

  const assignID = () => {
    const newID = window.prompt(`Enter Client ID for ${b.ownerFirstName} ${b.ownerLastName}:`);
    if (newID && newID.trim() !== "") {
      const cleanID = newID.trim();
      update(ref(db, 'bookings/' + b.id), { clientNumber: cleanID });
      if (b.userId) update(ref(db, `users/${b.userId}/profile`), { clientNumber: cleanID });
    }
  };

  const printKennelCard = () => {
    const printWindow = window.open('', '_blank', 'width=850,height=1100');
    printWindow.document.write(`
      <html>
        <head>
          <title>Door Card - ${b.petName}</title>
          <style>
            @page { size: portrait; margin: 0.4in; }
            body { font-family: sans-serif; margin: 0; padding: 0; text-align: center; color: black; }
            .border { border: 6px solid black; padding: 15px; height: 9.6in; box-sizing: border-box; display: flex; flex-direction: column; }
            .header-info { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 3px solid black; padding-bottom: 5px; margin-bottom: 10px; }
            h1 { font-size: 72pt; margin: 0; text-transform: uppercase; line-height: 1; font-weight: 900; }
            h2 { font-size: 26pt; margin: 5pt 0; line-height: 1.1; }
            .phone-text { font-size: 20pt; font-weight: bold; }
            .info-grid { display: grid; grid-template-columns: 1fr 1in 1fr; margin: 10pt 0; font-size: 16pt; text-align: left; border-bottom: 1px solid #ccc; padding-bottom: 10px; }
            .time-row { display: flex; justify-content: space-between; font-size: 18pt; text-align: left; font-weight: bold; margin-bottom: 15px; }
            .data-section { border: 3px solid black; margin: 6pt 0; padding: 6pt; font-size: 18pt; text-align: left; flex-grow: 1; display: flex; flex-direction: column; }
            .section-title { font-weight: 900; font-size: 13pt; text-decoration: underline; margin-bottom: 2pt; display: block; }
            .meds-box { background: #f2f2f2; border-width: 5px; }
            .footer { font-size: 10pt; color: #555; margin-top: auto; }
          </style>
        </head>
        <body onload="window.print()">
          <div class="border">
            <div class="header-info">
               <div style="text-align:left"><h2>${b.runType} ${b.sharedRunGroup && b.sharedRunGroup !== 'none' ? ` (${b.sharedRunGroup.toUpperCase()})` : ''}</h2></div>
               <div style="text-align:right"><span class="phone-text">📞 ${b.ownerPhone}</span></div>
            </div>
            <h1>${b.petName}</h1>
            <h2>${b.ownerLastName}, ${b.ownerFirstName}</h2>
            <div class="info-grid">
                <div><strong>IN:</strong><br/> ${b.startDate}</div>
                <div style="text-align:center"><strong>ID:</strong><br/>#${b.clientNumber || '---'}</div>
                <div style="text-align:right"><strong>OUT:</strong><br/> ${b.endDate}</div>
            </div>
            <div class="time-row">
                <div>${b.dropoffTime && b.dropoffTime !== "Unknown" ? '⬇️ Drop Time: ' + b.dropoffTime : ''}</div>
                <div style="text-align:right">${b.pickupTime && b.pickupTime !== "Unknown" ? '⬆️ Pickup Time: ' + b.pickupTime : ''}</div>
            </div>
            <div style="text-align:left; font-size:16pt; margin-bottom:10px;"><strong>🚨 EMERGENCY CONTACT:</strong> ${b.emergencyContact || 'None Provided'}</div>
            <div class="data-section meds-box"><span class="section-title">💊 MEDICATIONS / DOSAGE</span><div style="font-weight:bold; font-size: 22pt;">${b.meds || 'NONE'}</div></div>
            <div class="data-section"><span class="section-title">🧠 TEMPERAMENT & NOTES</span><div>${b.temperament || 'Standard'}</div></div>
            <div class="data-section" style="background:#fffde7;"><span class="section-title">🎒 BELONGINGS BROUGHT</span><div style="font-size: 16pt;">${b.belongings || 'None Listed'}</div></div>
            <div style="display:grid; grid-template-columns: 1fr 1fr; gap: 10pt;">
                <div class="data-section" style="flex-grow:0; min-height: 0.8in;"><span class="section-title">✂️ GROOMING</span><div style="font-size: 14pt;">${b.grooming || 'None'}</div></div>
                <div class="data-section" style="flex-grow:0; min-height: 0.8in;"><span class="section-title">🏥 PROCEDURES / VAX</span>
                   <div style="font-size: 14pt;">${b.procedures || 'None'}</div>
                   <div style="font-size: 14pt; margin-top: 5pt; font-weight: bold; border-top: 1px solid #ccc; padding-top: 2pt;">
                      ${b.incomingVaccines ? '💉 VAX: ' + b.incomingVaccines : ''}
                   </div>
                </div>
            </div>
            <div class="footer">🐾 Animal Clinic of Lawrenceville | Boarding Portal</div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const toggleComplete = (f) => update(ref(db, 'bookings/' + b.id), { [`${f}Done`]: !b[`${f}Done`] });
  const handleDelete = () => { if(window.confirm(`Delete ${b.petName}?`)) remove(ref(db, 'bookings/' + b.id)); };
  
  const cardColor = b.status === 'Checked-In' ? '#e8f5e9' : b.status === 'Checked-Out' ? '#ffebee' : 'white';
  const borderColor = b.status === 'Checked-In' ? '#2ecc71' : b.status === 'Checked-Out' ? '#e74c3c' : '#ddd';
  const isGoingHomeToday = b.endDate === filterDate.toDateString();

  return (
    <div style={{...staffEntryCard, backgroundColor: cardColor, borderColor: borderColor, marginBottom:'10px', position: 'relative'}}>
      <span onClick={handleDelete} style={deleteBtnStyle}>Delete</span>
      <div style={{fontSize:'10px', fontWeight:'bold', color:'#34495e', marginBottom:'5px'}}>🗓️ {b.startDate} — {b.endDate}</div>
      
      {isGoingHomeToday && (
        <div style={{display: 'flex', justifyContent: 'center', marginBottom: '10px'}}>
          <span style={{padding: '5px 14px', backgroundColor: '#e67e22', color: 'white', fontWeight: 'bold', borderRadius: '15px', fontSize: '13px', border: 'none', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
            👋 Going Home Today!
          </span>
        </div>
      )}

      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <div>
            <strong>{b.petName?.toUpperCase()}</strong> ({b.runType}) {b.sharedRunGroup && b.sharedRunGroup !== "none" && <span style={tagStyle}>Shared Run: {b.sharedRunGroup.toUpperCase()}</span>}
            {b.clientNumber ? <div style={{fontSize:'10px', color:'#2c3e50', fontWeight:'bold'}}>ID: #{b.clientNumber}</div> : <button onClick={assignID} style={{...statusBtn, backgroundColor:'#3498db', padding:'2px 5px', marginTop:'2px'}}>+ Client ID</button>}
            <div style={{fontSize:'10px', fontWeight:'bold', color: borderColor, marginTop:'2px'}}>{b.status.toUpperCase()}</div>
        </div>
        <div style={{display:'flex', gap:'5px'}}>
          <button onClick={printKennelCard} style={{...statusBtn, backgroundColor:'#7f8c8d'}}>🖨️ Door Card</button>
          <button onClick={() => setShowNotePopup(true)} style={{...statusBtn, backgroundColor: b.stayNotes ? '#f1c40f' : '#3498db', color: b.stayNotes ? '#2c3e50' : 'white'}}>{b.stayNotes ? 'Note' : '+ Note'}</button>
          <button onClick={() => setIsEditing(!isEditing)} style={{...statusBtn, backgroundColor:'#9b59b6'}}>{isEditing ? 'Close' : 'Edit'}</button>
          <button onClick={handleStatusChange} style={{...statusBtn, backgroundColor: b.status === 'Checked-In' ? '#e74c3c' : '#2ecc71'}}>{b.status === 'Approved' ? 'Arrived' : b.status === 'Checked-In' ? 'Departed' : 'Reset'}</button>
        </div>
      </div>
      
      <div style={{marginTop:'5px', display:'flex', flexWrap:'wrap', gap:'5px'}}>
        {b.dropoffTime && b.dropoffTime !== "Unknown" && <span style={{...tagStyle, backgroundColor:'#fff3e0', color:'#e67e22'}}>⬇️ Drop: {b.dropoffTime}</span>}
        {b.pickupTime && b.pickupTime !== "Unknown" && <span style={{...tagStyle, backgroundColor:'#fff3e0', color:'#e67e22'}}>⬆️ Pick: {b.pickupTime}</span>}
        {b.meds && <span style={{...tagStyle, backgroundColor:'#e3f2fd', color:'#1976d2'}}>💊 Meds</span>}
        {b.grooming && <span style={{...tagStyle, backgroundColor: b.groomingDone ? '#c8e6c9' : '#e1f5fe', color: b.groomingDone ? '#2e7d32' : '#0288d1'}}>{b.groomingDone ? '✅' : '✂️'} Grooming</span>}
        {b.procedures && <span style={{...tagStyle, backgroundColor: b.proceduresDone ? '#c8e6c9' : '#e0f2f1', color: b.proceduresDone ? '#2e7d32' : '#00796b'}}>{b.proceduresDone ? '✅' : '🏥'} Procedures</span>}
        {b.belongings && <span style={{...tagStyle, backgroundColor:'#fff9c4', color:'#856404'}}>🎒 Belongings</span>}
        {b.temperament && <span style={{...tagStyle, backgroundColor:'#f3e5f5', color:'#7b1fa2'}}>🧠 {b.temperament}</span>}
      </div>

      {showNotePopup && (
        <div style={modalOverlay}><div style={modalContent}>
            <h4>📝 Internal Stay Notes ({b.petName})</h4>
            <textarea style={{...inputStyle, height:'100px'}} defaultValue={b.stayNotes} onChange={e=>setTempNote(e.target.value)} />
            <div style={{display:'flex', gap:'10px'}}><button onClick={() => {update(ref(db, 'bookings/'+b.id), {stayNotes: tempNote}); setShowNotePopup(false);}} style={primaryBtn}>Save</button><button onClick={() => setShowNotePopup(false)} style={{...primaryBtn, backgroundColor:'#777'}}>Close</button></div>
        </div></div>
      )}

      {isEditing ? (
        <div style={{marginTop:'10px', borderTop:'1px solid #eee', paddingTop:'10px'}}>
           <div style={{display:'flex', gap:'5px'}}><input style={inputStyle} value={editData.ownerFirstName || ""} onChange={e => setEditData({...editData, ownerFirstName: e.target.value})} placeholder="First" /><input style={inputStyle} value={editData.ownerLastName || ""} onChange={e => setEditData({...editData, ownerLastName: e.target.value})} placeholder="Last" /></div>
           <div style={{display:'flex', gap:'5px'}}>
             <input style={{...inputStyle, flex: 1}} value={editData.petName} onChange={e => setEditData({...editData, petName: e.target.value})} placeholder="Pet Name" />
             <select style={{...inputStyle, flex: 1}} value={editData.runType || "Small Run"} onChange={e => setEditData({...editData, runType: e.target.value})}>
                {Object.keys(MAX_CAPS).map(run => <option key={run} value={run}>{run}</option>)}
             </select>
           </div>
           <input style={inputStyle} value={editData.ownerPhone || ""} onChange={e => setEditData({...editData, ownerPhone: formatPhone(e.target.value)})} placeholder="Phone" />
           <input style={inputStyle} value={editData.emergencyContact || ""} onChange={e => setEditData({...editData, emergencyContact: e.target.value})} placeholder="🚨 Emergency Contact" />
           
           <select style={inputStyle} value={editData.sharedRunGroup || "none"} onChange={e => setEditData({...editData, sharedRunGroup: e.target.value})}>
              <option value="none">Individual Run (Not Shared)</option>
              <option value="group1">Shared Run Group 1</option>
              <option value="group2">Shared Run Group 2</option>
              <option value="group3">Shared Run Group 3</option>
           </select>

           <div style={{display:'flex', gap:'5px'}}>
              <select style={inputStyle} value={editData.dropoffTime || "Unknown"} onChange={e=>setEditData({...editData, dropoffTime: e.target.value})}><option value="Unknown">Drop: Unknown</option><option value="8:00am AM">8:00am AM</option><option value="4:00pm PM">4:00pm PM</option></select>
              <select style={inputStyle} value={editData.pickupTime || "Unknown"} onChange={e=>setEditData({...editData, pickupTime: e.target.value})}><option value="Unknown">Pick: Unknown</option><option value="8:00am AM">8:00am AM</option><option value="4:00pm PM">4:00pm PM</option></select>
           </div>
           <Calendar selectRange={true} onChange={(d) => setEditData({...editData, startDate: d[0].toDateString(), startVal: d[0].getTime(), endDate: d[1].toDateString(), endVal: d[1].getTime()})} />
           <input style={inputStyle} value={editData.meds || ""} onChange={e => setEditData({...editData, meds: e.target.value})} placeholder="Meds" />
           <input style={inputStyle} value={editData.belongings || ""} onChange={e => setEditData({...editData, belongings: e.target.value})} placeholder="Belongings" />
           <input style={inputStyle} value={editData.grooming || ""} onChange={e => setEditData({...editData, grooming: e.target.value})} placeholder="Grooming" />
           <input style={inputStyle} value={editData.incomingVaccines || ""} onChange={e => setEditData({...editData, incomingVaccines: e.target.value})} placeholder="Vaccines" />
           <input style={inputStyle} value={editData.procedures || ""} onChange={e => setEditData({...editData, procedures: e.target.value})} placeholder="Procedures" />
           <input style={inputStyle} value={editData.temperament || ""} onChange={e => setEditData({...editData, temperament: e.target.value})} placeholder="Temperament" />
           <button onClick={saveEdit} style={{...primaryBtn, marginTop:'10px'}}>Save All Changes</button>
        </div>
      ) : (
        <div style={{marginTop:'10px'}}>
            {b.meds && <div style={infoBox}><strong>Meds:</strong> {b.meds}</div>}
            {b.belongings && <div style={{...infoBox, borderLeftColor:'#f1c40f', backgroundColor:'#fff9c4'}}><strong>Belongings:</strong> {b.belongings}</div>}
            {b.grooming && (<div style={{...infoBox, borderLeftColor: b.groomingDone ? '#2ecc71' : '#03a9f4', backgroundColor: b.groomingDone ? '#f1fcf1' : '#e1f5fe', display:'flex', justifyContent:'space-between', alignItems:'center'}}><span>Grooming: {b.grooming} {b.groomingDone && '✅'}</span><button onClick={() => toggleComplete('grooming')} style={doneBtnStyle}>{b.groomingDone ? 'Undo' : 'Done'}</button></div>)}
            {b.procedures && (<div style={{...infoBox, borderLeftColor: b.proceduresDone ? '#2ecc71' : '#38b2ac', backgroundColor: b.proceduresDone ? '#f1fcf1' : '#e6fffa', display:'flex', justifyContent:'space-between', alignItems:'center'}}><span>Procedures: {b.procedures} {b.proceduresDone && '✅'}</span><button onClick={() => toggleComplete('procedures')} style={doneBtnStyle}>{b.proceduresDone ? 'Undo' : 'Done'}</button></div>)}
            {b.incomingVaccines && <div style={{...infoBox, borderLeftColor:'#e91e63', backgroundColor:'#fff0f5'}}><strong>Vaccines Due:</strong> {b.incomingVaccines}</div>}
        </div>
      )}
    </div>
  );
}

// ==========================================
// SECTION 7: MASTER BOOKING FORM
// ==========================================
function BookingForm({ userId, profile, allProfiles, allBookings, bannedList, close, isStaff = false }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [first, setFirst] = useState(profile?.firstName || "");
  const [last, setLast] = useState(profile?.lastName || "");
  const [phone, setPhone] = useState(profile?.phone || "");
  const [emergency, setEmergency] = useState(profile?.emergencyContact || "");
  const [clientID, setClientID] = useState(profile?.clientNumber || "");
  const [searchResults, setSearchResults] = useState([]);
  const [activeProfile, setActiveProfile] = useState(profile || null);
  const [dates, setDates] = useState([new Date(), new Date()]);
  const [pets, setPets] = useState([{ name: "", run: "Small Run", sharedRunGroup: "none", meds: "", belongings: "", grooming: "", vaccines: "", procedures: "", temperament: "", dropoffTime: "Unknown", pickupTime: "Unknown", confirmVaccinePolicy: false }]);

  // Dedicated Roster Search
  const handleSearch = (val) => {
    setSearchQuery(val);
    const s = val.toString().trim().toLowerCase();
    const cleanS = cleanDigits(val);
    if (!s) { setSearchResults([]); return; }

    const matches = allProfiles.filter(p => {
      const matchId = p.clientNumber?.toString().toLowerCase().includes(s);
      const matchLast = p.lastName?.toLowerCase().includes(s);
      const matchFirst = p.firstName?.toLowerCase().includes(s);
      const matchPhone = cleanS && cleanDigits(p.phone).includes(cleanS);
      return matchId || matchLast || matchFirst || matchPhone;
    });

    setSearchResults(matches);
  };

  const selectClient = (p) => {
    setFirst(p.firstName || ""); 
    setLast(p.lastName || ""); 
    setPhone(formatPhone(p.phone) || ""); 
    setEmergency(p.emergencyContact || ""); 
    setClientID(p.clientNumber || ""); 
    setActiveProfile(p); 
    setSearchQuery("");
    setSearchResults([]);
  };

  const selectRegisteredPet = (idx, pet) => {
    if (!pet) return;
    const n = [...pets];
    n[idx].name = pet.name; 
    n[idx].run = pet.run; 
    n[idx].temperament = pet.temperament || ""; 
    n[idx].meds = pet.meds || "";
    setPets(n);
  };

  // Direct Pet Deletion from Selected Client's Profile
  const deletePetFromRoster = async (petKey) => {
    if (!activeProfile?.uid) return;
    if (window.confirm(`Permanently remove this pet from ${activeProfile.firstName} ${activeProfile.lastName}'s master profile?`)) {
      await remove(ref(db, `users/${activeProfile.uid}/profile/pets/${petKey}`));
      const updated = { ...activeProfile };
      if (updated.pets) delete updated.pets[petKey];
      setActiveProfile(updated);
    }
  };

  const updatePet = (i, f, v) => { const n = [...pets]; n[i][f] = v; setPets(n); };
  const addPetBox = () => setPets([...pets, { name: "", run: "Small Run", sharedRunGroup: "none", meds: "", belongings: "", grooming: "", vaccines: "", procedures: "", temperament: "", dropoffTime: "Unknown", pickupTime: "Unknown", confirmVaccinePolicy: false }]);

  const submit = async () => {
    const batchId = Date.now();
    for (let p of pets) { 
      if (!p.name) continue; 
      if (!p.confirmVaccinePolicy) return alert(`Please certify the vaccine policy check box for ${p.name}.`); 
      const isBanned = bannedList.some(b => b.petName.toLowerCase().trim() === p.name.toLowerCase().trim() && b.ownerLastName.toLowerCase().trim() === last.toLowerCase().trim());
      if (isBanned) return alert(`🛑 BLOCKING BOOKING: ${p.name} is on the Banned List.`);
    }

    const cleanPh = cleanDigits(phone);
    const cleanId = cleanKey(clientID);

    let targetUid = userId;
    if (!targetUid || targetUid === "STAFF") {
      if (activeProfile?.uid) targetUid = activeProfile.uid;
      else if (cleanId) targetUid = `CLIENT-${cleanId}`;
      else if (cleanPh) targetUid = `CLIENT-${cleanPh}`;
      else targetUid = `CLIENT-${Date.now()}`;
    }

    await set(ref(db, `users/${targetUid}/profile`), {
      firstName: first,
      lastName: last,
      phone: formatPhone(phone),
      emergencyContact: emergency,
      clientNumber: clientID ? clientID.trim() : '',
      uid: targetUid,
      pets: activeProfile?.pets || {}
    });

    pets.forEach(async (p, index) => {
      if (!p.name) return;
      const petKey = cleanKey(p.name) || `pet-${index}`;

      update(ref(db, `users/${targetUid}/profile/pets/${petKey}`), { 
        name: p.name.trim(), 
        run: p.run, 
        temperament: p.temperament, 
        meds: p.meds 
      });

      set(ref(db, 'bookings/' + `${batchId}-${index}`), {
        userId: targetUid, 
        petName: p.name, 
        runType: p.run, 
        sharedRunGroup: p.sharedRunGroup, 
        meds: p.meds, 
        belongings: p.belongings, 
        grooming: p.grooming, 
        incomingVaccines: p.vaccines, 
        procedures: p.procedures, 
        temperament: p.temperament,
        dropoffTime: p.dropoffTime, 
        pickupTime: p.pickupTime, 
        ownerFirstName: first, 
        ownerLastName: last, 
        ownerPhone: formatPhone(phone), 
        emergencyContact: emergency, 
        clientNumber: clientID ? clientID.trim() : '',
        startDate: dates[0].toDateString(), 
        startVal: dates[0].getTime(), 
        endDate: dates[1].toDateString(), 
        endVal: dates[1].getTime(), 
        status: isStaff ? "Approved" : "Pending"
      });
    });
    close();
  };

  const isWEStart = dates[0].getDay() === 0 || dates[0].getDay() === 6;
  const isWEEnd = dates[1].getDay() === 0 || dates[1].getDay() === 6;

  return (
    <div style={{marginTop:'15px', borderTop:'1px solid #ddd', paddingTop:'15px', position:'relative'}}>
      {isStaff && (
        <div style={{position:'relative', marginBottom: '10px'}}>
          <input 
            style={{...inputStyle, backgroundColor:'#fff9c4'}} 
            placeholder="🔍 Search Roster by ID, Last Name, or Phone #" 
            value={searchQuery} 
            onChange={e => handleSearch(e.target.value)} 
          />
          {searchResults.length > 0 && (
            <div style={dropdownListStyle}>
              {searchResults.map((p, idx) => (
                <div key={idx} onClick={() => selectClient(p)} style={dropdownItemStyle}>
                  <strong>{p.clientNumber ? `ID: #${p.clientNumber} — ` : ''}{p.lastName}, {p.firstName}</strong> <span style={{fontSize: '10px', color: '#666'}}>({formatPhone(p.phone)})</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Staff Pet Removal Manager for Loaded Client */}
      {isStaff && activeProfile?.pets && Object.keys(activeProfile.pets).length > 0 && (
        <div style={{background: '#fff3cd', padding: '8px', borderRadius: '6px', marginBottom: '10px', border: '1px solid #ffeeba'}}>
          <div style={{fontSize: '11px', fontWeight: 'bold', color: '#856404', marginBottom: '5px'}}>
            🐾 Saved Pets on {activeProfile.firstName}'s Profile (Click to Remove):
          </div>
          <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
            {Object.keys(activeProfile.pets).map(pKey => (
              <span key={pKey} style={{fontSize: '11px', background: '#fff', padding: '3px 6px', borderRadius: '4px', border: '1px solid #ddd', display: 'flex', alignItems: 'center', gap: '5px'}}>
                <strong>{activeProfile.pets[pKey].name}</strong> ({activeProfile.pets[pKey].run})
                <button 
                  type="button" 
                  onClick={() => deletePetFromRoster(pKey)} 
                  style={{border: 'none', background: '#e74c3c', color: '#fff', borderRadius: '3px', fontSize: '9px', cursor: 'pointer', padding: '1px 4px'}}
                >
                  Delete
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Dedicated Client ID & Details Inputs */}
      <div style={{display:'flex', gap:'5px'}}>
        <input 
          placeholder="Client ID #" 
          value={clientID} 
          onChange={e => setClientID(e.target.value)} 
          style={{...inputStyle, flex: 1, backgroundColor: '#fdfefe', border: '1px solid #95a5a6', fontWeight: 'bold'}} 
        />
        <input placeholder="First Name" value={first} onChange={e=>setFirst(e.target.value)} style={{...inputStyle, flex: 1}} />
        <input placeholder="Last Name" value={last} onChange={e=>setLast(e.target.value)} style={{...inputStyle, flex: 1}} />
      </div>
      <input placeholder="Phone" value={phone} onChange={e=>setPhone(formatPhone(e.target.value))} style={inputStyle} />
      <input placeholder="🚨 Emergency Name/Number" value={emergency} onChange={e=>setEmergency(e.target.value)} style={inputStyle} />
      
      <div style={{margin:'15px 0', padding:'10px', backgroundColor:'#f0f4f8', borderRadius:'8px'}}>
        {pets.map((p, i) => (
          <div key={i} style={{marginBottom:'15px', paddingBottom:'15px', borderBottom:'1px dashed #ccc'}}>
            {(activeProfile?.pets || profile?.pets) && (
              <select style={{...inputStyle, backgroundColor:'#e3f2fd'}} onChange={(e) => selectRegisteredPet(i, (activeProfile?.pets || profile?.pets)[e.target.value])}>
                <option value="">— Select a saved pet (optional) —</option>
                {Object.keys(activeProfile?.pets || profile?.pets).map(k => <option key={k} value={k}>{(activeProfile?.pets || profile?.pets)[k].name}</option>)}
              </select>
            )}
            <input placeholder="Pet Name" value={p.name} onChange={e=>updatePet(i, 'name', e.target.value)} style={inputStyle} />
            <select value={p.run} onChange={e=>updatePet(i, 'run', e.target.value)} style={inputStyle}>{Object.keys(MAX_CAPS).map(run => <option key={run} value={run}>{run}</option>)}</select>
            
            <select style={inputStyle} value={p.sharedRunGroup} onChange={e => updatePet(i, 'sharedRunGroup', e.target.value)}>
              <option value="none">Individual Run (Not Shared)</option>
              <option value="group1">Shared Run Group 1</option>
              <option value="group2">Shared Run Group 2</option>
              <option value="group3">Shared Run Group 3</option>
            </select>

            <label style={{ ...vaxLabel, display: 'flex', alignItems: 'flex-start', marginTop: '8px', background: '#fff', padding: '10px', borderRadius: '5px', border: '1px solid #ddd', cursor: 'pointer' }}>
              <input type="checkbox" checked={p.confirmVaccinePolicy} onChange={e => updatePet(i, 'confirmVaccinePolicy', e.target.checked)} style={{ marginRight: '8px', marginTop: '2px' }} />
              <span style={{ fontSize: '11px', lineHeight: '1.3' }}>I certify that this pet is current on all required vaccines (Rabies, Distemper, Bordetella). If they are not current, I understand they will be vaccinated upon arrival at the owner's expense.</span>
            </label>

            <div style={{display:'flex', gap:'5px', marginBottom:'8px', marginTop: '8px'}}>
              {isWEStart && <div style={{flex:1}}><label style={{fontSize:10}}>Drop:</label><select style={inputStyle} value={p.dropoffTime} onChange={e=>updatePet(i, 'dropoffTime', e.target.value)}><option value="Unknown">Unknown</option><option value="8:00am AM">8:00am AM</option><option value="4:00pm PM">4:00pm PM</option></select></div>}
              {isWEEnd && <div style={{flex:1}}><label style={{fontSize:10}}>Pick:</label><select style={inputStyle} value={p.pickupTime} onChange={e=>updatePet(i, 'pickupTime', e.target.value)}><option value="Unknown">Unknown</option><option value="8:00am AM">8:00am AM</option><option value="4:00pm PM">4:00pm PM</option></select></div>}
            </div>
            <input placeholder="Meds" value={p.meds} onChange={e=>updatePet(i, 'meds', e.target.value)} style={inputStyle} />
            <input placeholder="Belongings" value={p.belongings} onChange={e=>updatePet(i, 'belongings', e.target.value)} style={inputStyle} />
            <input placeholder="Grooming" value={p.grooming} onChange={e=>updatePet(i, 'grooming', e.target.value)} style={inputStyle} />
            <input placeholder="Procedures" value={p.procedures} onChange={e=>updatePet(i, 'procedures', e.target.value)} style={inputStyle} />
            <input placeholder="Vaccines Due" value={p.vaccines} onChange={e=>updatePet(i, 'vaccines', e.target.value)} style={inputStyle} />
            <input placeholder="Temperament" value={p.temperament} onChange={e=>updatePet(i, 'temperament', e.target.value)} style={inputStyle} />
          </div>
        ))}
        <button onClick={addPetBox} style={{...statusBtn, backgroundColor:'#34495e', width:'100%'}}>+ Add Another Pet</button>
      </div>
      <Calendar onChange={setDates} selectRange={true} />
      <button onClick={submit} style={{...primaryBtn, marginTop:'15px'}}>Save Booking & Profile</button>
    </div>
  );
}

// ==========================================
// SECTION 8: OWNER DASHBOARD
// ==========================================
function OwnerDashboard({ user, allBookings, profile, allProfiles, bannedList }) {
  const [showForm, setShowForm] = useState(false);
  const [checkDate, setCheckDate] = useState(new Date());
  const [petName, setPetName] = useState("");
  const [petRun, setPetRun] = useState("Small Run");
  const [petTemp, setPetTemp] = useState("");
  const [petMeds, setPetMeds] = useState("");

  const myBookings = allBookings.filter(b => b.userId === user.uid);
  const dailyAtCheck = allBookings.filter(b => b.status !== "Pending" && b.status !== "Declined" && b.status !== "Checked-Out" && checkDate.getTime() >= b.startVal && checkDate.getTime() <= b.endVal);

  const addPetToProfile = () => {
    if (!petName) return alert("Enter pet name");
    const petKey = cleanKey(petName) || 'pet';
    update(ref(db, `users/${user.uid}/profile/pets/${petKey}`), { name: petName, run: petRun, temperament: petTemp, meds: petMeds });
    setPetName(""); setPetTemp(""); setPetMeds("");
  };

  return (
    <div style={containerStyle}>
       <div style={{maxWidth:'600px', width:'100%'}}>
          <div style={{...cardStyle, marginBottom:'20px'}}>
            <h3>🐾 My Registered Pets</h3>
            <div style={{display:'flex', gap:'5px', marginBottom:'10px'}}>
              <input placeholder="Pet Name" value={petName} onChange={e=>setPetName(e.target.value)} style={inputStyle} />
              <select value={petRun} onChange={e=>setPetRun(e.target.value)} style={inputStyle}>{Object.keys(MAX_CAPS).map(run => <option key={run} value={run}>{run}</option>)}</select>
            </div>
            <input placeholder="Daily Meds" value={petMeds} onChange={e=>setPetMeds(e.target.value)} style={inputStyle} />
            <input placeholder="Temperament" value={petTemp} onChange={e=>setPetTemp(e.target.value)} style={inputStyle} />
            <button onClick={addPetToProfile} style={{...statusBtn, width:'100%', padding:'8px'}}>+ Register Pet</button>
            <div style={{marginTop:'10px'}}>
              {profile?.pets && Object.keys(profile.pets).map(k => (
                <div key={k} style={{fontSize:12, padding:'5px', borderBottom:'1px solid #eee'}}>
                  <strong>{profile.pets[k].name}</strong> ({profile.pets[k].run})
                  <span onClick={()=>remove(ref(db, `users/${user.uid}/profile/pets/${k}`))} style={{color:'red', cursor:'pointer', float:'right'}}>Delete</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{...cardStyle, marginBottom:'20px'}}>
            <h4>📅 Check Availability</h4>
            <Calendar onChange={setCheckDate} value={checkDate} /><div style={{marginTop:'15px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px'}}>{Object.keys(MAX_CAPS).map(run => { const u = dailyAtCheck.filter(b => b.runType === run).length; return <div key={run} style={{padding:'8px', background:'#f8f9fa', borderRadius:'6px', border:'1px solid #eee'}}>{run}: <strong>{MAX_CAPS[run] - u > 0 ? `${MAX_CAPS[run] - u} Open` : 'Full'}</strong></div>; })}</div>
          </div>
          <div style={cardStyle}>
            <h3>My Pet Stays</h3>
            {myBookings.map(b => (
              <div key={b.id} style={{...statusCard, borderLeft: b.status === 'Declined' ? '4px solid #e74c3c' : '4px solid #3498db'}}>
                <strong>{b.petName}</strong> — <span style={{fontSize:'12px', color: b.status === 'Declined' ? '#e74c3c' : '#3498db', fontWeight:'bold'}}>{b.status}</span>
                <div style={{fontSize:'11px', color:'#777'}}>{b.startDate} to {b.endDate}</div>
                {b.status === 'Declined' && b.declineReason && (
                   <div style={{marginTop:'5px', fontSize:'12px', color:'#e74c3c', background:'#fff5f5', padding:'5px', borderRadius:'4px', border:'1px solid #feb2b2'}}>
                     <strong>Reason:</strong> {b.declineReason}
                   </div>
                )}
              </div>
            ))}
            <button onClick={() => setShowForm(!showForm)} style={{...primaryBtn, marginTop:'10px'}}>{showForm ? "Cancel" : "+ Request New Boarding"}</button>
            {showForm && <BookingForm userId={user.uid} profile={profile} allProfiles={allProfiles} allBookings={allBookings} bannedList={bannedList} close={() => setShowForm(false)} />}
          </div>
       </div>
    </div>
  );
}

// ==========================================
// SECTION 9: AUTHENTICATION
// ==========================================
function Login({ setView }) {
  const [email, setEmail] = useState(""); const [pass, setPass] = useState("");
  return (<div style={{width:'300px'}}><h3>Login</h3><input style={inputStyle} placeholder="Email" onChange={e=>setEmail(e.target.value)}/><input style={inputStyle} type="password" placeholder="Pass" onChange={e=>setPass(e.target.value)}/><button onClick={()=>signInWithEmailAndPassword(auth, email, pass)} style={primaryBtn}>Login</button><p onClick={()=>setView('signup')} style={linkStyle}>Sign Up</p></div>);
}

function Signup({ setView, allProfiles }) {
  const [email, setEmail] = useState(""); const [pass, setPass] = useState(""); const [fN, setFn] = useState(""); const [lN, setLn] = useState(""); const [ph, setPh] = useState(""); const [emg, setEmg] = useState("");
  
  const handle = async () => {
    try {
      const res = await createUserWithEmailAndPassword(auth, email, pass);
      const newUid = res.user.uid; 
      const cleanPh = cleanDigits(ph);
      const cleanLast = cleanKey(lN);
      const cleanFirst = cleanKey(fN);

      const snap = await get(ref(db, 'users/'));
      const rawUsers = snap.val() || {};

      for (let key of Object.keys(rawUsers)) {
        if (key === newUid) continue;
        const cProfile = rawUsers[key]?.profile;
        if (!cProfile) continue;

        const cPhone = cleanDigits(cProfile.phone);
        const cLast = cleanKey(cProfile.lastName);
        const cFirst = cleanKey(cProfile.firstName);

        const isMatch = (cleanPh && cPhone && cleanPh === cPhone) || (cleanLast && cLast && cleanLast === cLast && cleanFirst && cFirst && cleanFirst === cFirst);

        if (isMatch) {
          if (cProfile.pets) {
            for (let petKey of Object.keys(cProfile.pets)) {
              await set(ref(db, `users/${newUid}/profile/pets/${petKey}`), cProfile.pets[petKey]);
            }
          }
          if (cProfile.clientNumber) {
            await update(ref(db, `users/${newUid}/profile`), { clientNumber: cProfile.clientNumber });
          }
          const bSnap = await get(ref(db, 'bookings/'));
          const bks = bSnap.val() || {};
          for (let k of Object.keys(bks)) {
            if (bks[k].userId === key) {
              await update(ref(db, `bookings/${k}`), { userId: newUid });
            }
          }
          await remove(ref(db, `users/${key}`));
        }
      }

      await set(ref(db, `users/${newUid}/profile`), { 
        firstName: fN, 
        lastName: lN, 
        phone: formatPhone(ph), 
        emergencyContact: emg, 
        uid: newUid 
      });
    } catch (err) { alert(err.message); }
  };

  return (<div style={{width:'300px'}}><h3>Sign Up</h3><input style={inputStyle} placeholder="First Name" onChange={e=>setFn(e.target.value)}/><input style={inputStyle} placeholder="Last Name" onChange={e=>setLn(e.target.value)}/><input style={inputStyle} placeholder="Phone" value={ph} onChange={e=>setPh(formatPhone(e.target.value))}/><input style={inputStyle} placeholder="🚨 Emergency Name/Number" onChange={e=>setEmg(e.target.value)}/><input style={inputStyle} placeholder="Email" onChange={e=>setEmail(e.target.value)}/><input style={inputStyle} type="password" placeholder="Pass" onChange={e=>setPass(e.target.value)}/><button onClick={handle} style={primaryBtn}>Create Account</button></div>);
}

// ==========================================
// SECTION 10: CSS STYLES
// ==========================================
const pageLayout = { backgroundColor:'#f4f7f6', minHeight:'100vh', fontFamily:'sans-serif' };
const headerStyle = { background:'#1a5276', color:'white', padding:'10px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' };
const logoutSmall = { padding:'5px 10px', background:'#e74c3c', color:'white', border:'none', borderRadius:'5px', cursor:'pointer' };
const containerStyle = { display:'flex', justifyContent:'center', padding:'20px' };
const cardStyle = { backgroundColor:'white', padding:'15px', borderRadius:'12px', boxShadow:'0 4px 6px rgba(0,0,0,0.1)' };
const inputStyle = { width:'100%', padding:'8px', marginBottom:'8px', borderRadius:'5px', border:'1px solid #ccc', boxSizing:'border-box', fontSize:'13px' };
const primaryBtn = { width:'100%', padding:'10px', color:'white', border:'none', borderRadius:'5px', fontWeight:'bold', cursor:'pointer', backgroundColor:'#3498db' };
const statusCard = { padding:'10px', border:'1px solid #eee', borderRadius:'8px', marginBottom:'8px' };
const statusBtn = { color:'white', border:'none', padding:'4px 8px', borderRadius:'4px', cursor:'pointer', fontSize:'11px', backgroundColor:'#2ecc71' };
const staffEntryCard = { padding:'10px', border:'1px solid #ddd', borderRadius:'8px', borderWidth:'2px' };
const miniActionCard = { padding:'10px', border:'1px solid #eee', borderRadius:'8px', marginBottom:'10px' };
const groupContainer = { border:'1px solid #34495e', borderRadius:'8px', padding:'10px', marginBottom:'15px', backgroundColor:'#f9f9f9' };
const groupHeader = { fontSize:'14px', fontWeight:'bold', color:'#2c3e50' };
const tagStyle = { padding: '3px 8px', borderRadius: '15px', fontSize: '10px', border:'1px solid #ddd' };
const infoBox = { padding:'6px', margin:'4px 0', fontSize:'12px', borderRadius:'4px', borderLeft:'4px solid #3498db', backgroundColor:'#f0f7ff' };
const linkStyle = { fontSize:'12px', cursor:'pointer', textAlign:'center', marginTop:'15px', color:'#3498db' };
const editLabel = { fontSize:'11px', fontWeight:'bold', display:'block', marginBottom:'5px', color:'#777', marginTop:'8px' };
const vaxLayoutContainer = { display: 'flex', gap: '10px', margin: '10px 0', padding: '10px', background: '#fff', borderRadius: '5px', border: '1px solid #ddd', alignItems: 'flex-start' };
const vaxColumnLeft = { flex: 2 };
const vaxColumnRight = { flex: 1, borderLeft: '1px solid #eee', paddingLeft: '10px' };
const vaxLabel = { fontSize: '11px', display: 'flex', alignItems: 'flex-start', marginBottom: '8px', cursor: 'pointer', lineHeight: '1.2' };
const deleteBtnStyle = { position: 'absolute', top: '10px', right: '10px', color: '#e74c3c', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', padding: '2px 5px', border: '1px solid #e74c3c', borderRadius: '3px' };
const doneBtnStyle = { background: '#fff', border: '1px solid #ccc', borderRadius: '4px', padding: '2px 6px', fontSize: '10px', cursor: 'pointer', marginLeft: '10px' };
const modalOverlay = { position:'fixed', top:0, left:0, width:'100%', height:'100%', background:'rgba(0,0,0,0.5)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:1000 };
const modalContent = { background:'white', padding:'20px', borderRadius:'10px', width:'350px', boxShadow:'0 5px 15px rgba(0,0,0,0.3)' };
const dropdownListStyle = { position:'absolute', top:'100%', left:0, right:0, background:'white', border:'1px solid #ccc', borderRadius:'5px', zIndex:10, maxHeight:'150px', overflowY:'auto', boxShadow:'0 4px 6px rgba(0,0,0,0.1)' };
const dropdownItemStyle = { padding:'8px', borderBottom:'1px solid #eee', cursor:'pointer', fontSize:'12px', textAlign:'left', color: 'black' };

export default App;